/**
 * OcrolusAuthenticityCard — one row per document Ocrolus analyzed, with its
 * authenticity score, rating, reason codes and risk signals.
 *
 * Everything comes from `signals.documents[]` on GET /files/ocrolus_book/{dealId}
 * (graded on Ocrolus's own scale since 2026-10-15 — see §2a of eastview-os
 * frontend_documentation/ocrolus-book-and-notes.md). Books graded before that
 * (`gradingVersion == null`) have no per-document detail, so the card says so
 * and points at the re-sync action instead of listing anything.
 *
 * Ocrolus only reports a document's NAME, not our loan-document id, so a row
 * links to a preview only when that name resolves to exactly one of the loan's
 * documents; otherwise the name is plain text.
 */
import { useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import { Button, Chip, IconButton, Tooltip } from "@mui/material";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import VerifiedUserOutlined from "@mui/icons-material/VerifiedUserOutlined";

import AgGridTableWrapper from "../../../../../components/agGrid/AgGridTableWrapper";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import {
  ActionCell,
  actionColumn,
} from "../../../../admin/shared/adminTableActions";
import colors, { status as statusColors } from "../../../../../theme/colors";
import {
  AUTHENTICITY_STYLE,
  OCROLUS_COL_DEF,
  OCROLUS_GRID_ROW_H,
  OCROLUS_GRID_SX,
  OcrolusCard,
  OcrolusEmptyState,
  formatReasonCode,
  isLegacyGrading,
  sortAuthenticityDocs,
} from "./ocrolusShared";

// Names compared without case or extension — Ocrolus keeps the uploaded file
// name, which is usually our `fileName` but sometimes the display name.
const normName = (s) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .replace(/\.[a-z0-9]{2,5}$/, "");

const ScoreBadge = ({ score, authenticity }) => {
  const s = AUTHENTICITY_STYLE[authenticity] || statusColors.neutral;
  return (
    <Flx ac jc sx={{ height: "100%" }}>
      <Flx
        ac
        jc
        sx={{
          minWidth: 34,
          height: 22,
          px: 0.75,
          borderRadius: "6px",
          fontSize: 12,
          fontWeight: 700,
          color: colors.white,
          background: s.solid,
        }}
      >
        {score ?? "—"}
      </Flx>
    </Flx>
  );
};

const DocumentNameCell = ({ data, resolveDoc, onPreview }) => {
  const label = data?.name || data?.docType || "Unnamed document";
  const doc = resolveDoc(data?.name);
  return (
    <Flx column g={0.15} sx={{ height: "100%", justifyContent: "center", py: 0.5 }}>
      <Txt
        onClick={
          doc
            ? (e) => {
                e.stopPropagation();
                onPreview(doc);
              }
            : undefined
        }
        sx={{
          fontSize: 13,
          fontWeight: 500,
          color: colors.ink,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          ...(doc
            ? {
                cursor: "pointer",
                "&:hover": { color: colors.action, textDecoration: "underline" },
              }
            : null),
        }}
      >
        {label}
      </Txt>
      {data?.name && data?.docType && (
        <Txt sx={{ fontSize: 11, color: colors.slate500 }}>
          {formatReasonCode(data.docType)}
        </Txt>
      )}
    </Flx>
  );
};

const WrapText = ({ children, muted }) => (
  <Flx ac sx={{ height: "100%" }}>
    <Txt
      sx={{
        fontSize: 12.5,
        lineHeight: 1.35,
        color: muted ? colors.slate400 : colors.slate700,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        whiteSpace: "normal",
      }}
    >
      {children}
    </Txt>
  </Flx>
);

const OcrolusAuthenticityCard = ({ signals, loanDocuments, onResync }) => {
  const dispatch = useDispatch();
  const legacy = isLegacyGrading(signals);

  const rows = useMemo(
    // Ocrolus gives no per-document id; the sorted position is the row key.
    () =>
      sortAuthenticityDocs(signals?.documents).map((d, i) => ({
        ...d,
        _rowKey: `${i}:${d?.name || d?.docType || ""}`,
      })),
    [signals?.documents],
  );

  // name → loan document, only where the name is unambiguous.
  const docsByName = useMemo(() => {
    const map = new Map();
    (Array.isArray(loanDocuments) ? loanDocuments : []).forEach((d) => {
      const keys = new Set(
        [d?.fileName, d?.file_display_name].map(normName).filter(Boolean),
      );
      keys.forEach((k) => map.set(k, map.has(k) ? null : d));
    });
    return map;
  }, [loanDocuments]);

  const resolveDoc = useCallback(
    (name) => (name ? docsByName.get(normName(name)) || null : null),
    [docsByName],
  );

  const handlePreview = useCallback(
    (doc) => doc && dispatch(filePreviewSetData(doc)),
    [dispatch],
  );

  const columnDefs = useMemo(
    () => [
      {
        field: "score",
        headerName: "Score",
        width: 80,
        cellRenderer: (p) => (
          <ScoreBadge score={p.value} authenticity={p.data?.authenticity} />
        ),
      },
      {
        colId: "name",
        headerName: "Document",
        flex: 2,
        minWidth: 220,
        valueGetter: (p) => p.data?.name || p.data?.docType || "",
        cellRenderer: (p) => (
          <DocumentNameCell
            data={p.data}
            resolveDoc={resolveDoc}
            onPreview={handlePreview}
          />
        ),
      },
      {
        field: "authenticity",
        headerName: "Authenticity",
        width: 120,
        cellRenderer: (p) => {
          if (!p.value) return <WrapText muted>—</WrapText>;
          const s = AUTHENTICITY_STYLE[p.value] || statusColors.neutral;
          return (
            <Flx ac sx={{ height: "100%" }}>
              <Chip
                size="small"
                label={p.value}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: s.bg,
                  color: s.fg,
                }}
              />
            </Flx>
          );
        },
      },
      {
        colId: "reasonCodes",
        headerName: "Reason codes",
        flex: 1.3,
        minWidth: 160,
        valueGetter: (p) =>
          (p.data?.reasonCodes || []).map(formatReasonCode).join(", "),
        tooltipValueGetter: (p) => p.value || null,
        cellRenderer: (p) => (
          <WrapText muted={!p.value}>{p.value || "—"}</WrapText>
        ),
      },
      {
        colId: "riskSignals",
        headerName: "Risk signals",
        flex: 1.5,
        minWidth: 180,
        valueGetter: (p) => (p.data?.riskSignals || []).join(", "),
        tooltipValueGetter: (p) => p.value || null,
        cellRenderer: (p) => (
          <WrapText muted={!p.value}>{p.value || "—"}</WrapText>
        ),
      },
      actionColumn({
        colId: "dashboard",
        cellRenderer: (p) =>
          p.data?.dashboardUrl ? (
            <ActionCell>
              <Tooltip title="Open in Ocrolus">
                <IconButton
                  component="a"
                  href={p.data.dashboardUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="Open in Ocrolus"
                >
                  <OpenInNewRounded sx={{ fontSize: 16, color: colors.slate500 }} />
                </IconButton>
              </Tooltip>
            </ActionCell>
          ) : null,
      }),
    ],
    [resolveDoc, handlePreview],
  );

  const needReview = signals?.reviewDocuments || 0;
  const subtitle = legacy
    ? "Graded before the current scale"
    : rows.length
      ? needReview > 0
        ? `${needReview} of ${rows.length} need review`
        : `All ${rows.length} passed`
      : undefined;

  return (
    <OcrolusCard title="Document authenticity" subtitle={subtitle}>
      {legacy ? (
        <OcrolusEmptyState
          icon={VerifiedUserOutlined}
          title="Re-sync with Ocrolus to regrade"
          action={
            onResync && (
              <Button variant="text" size="small" onClick={onResync}>
                Re-sync with Ocrolus
              </Button>
            )
          }
        >
          This book was graded before Ocrolus's per-document authenticity
          scores were used, so there is no document-by-document detail yet.
        </OcrolusEmptyState>
      ) : (
        <AgGridTableWrapper
          compactTable
          noBorder
          height={rows.length > 8 ? 400 : "auto"}
          sx={OCROLUS_GRID_SX}
        >
          <AgGridReact
            rowData={rows}
            columnDefs={columnDefs}
            getRowId={(p) => p.data?._rowKey}
            rowHeight={OCROLUS_GRID_ROW_H}
            domLayout={rows.length > 8 ? undefined : "autoHeight"}
            defaultColDef={OCROLUS_COL_DEF}
            tooltipShowDelay={300}
            suppressCellFocus
          />
        </AgGridTableWrapper>
      )}
    </OcrolusCard>
  );
};

export default OcrolusAuthenticityCard;
